/****************************************************************************************/
/* Helper for the TranslationEditor.                                                    */
/*                                                                                      */
/* The app loads two layers of texts (see contexts/LanguageContext):                    */
/*   1. src/lang/<language>.json  -> namespace "translation" (shipped with the build)    */
/*   2. <cdn>/<className>/<language>-translations.json -> namespace <className>          */
/*                                                                                      */
/* Layer 2 wins, layer 1 is the fallback namespace. Layer 2 files therefore have no      */
/* "translation" root and only contain the keys a derivative wants to change.            */
/****************************************************************************************/

/** Languages we ship a main translation file for. */
export const MAIN_LANGUAGES = ['de', 'de-CH', 'en', 'es', 'fr', 'it', 'nl'];

/** Language i18next falls back to when a key is missing. */
export const FALLBACK_LANGUAGE = 'de';

/** Keys that live inside the override files but are no translations. */
export const META_KEYS = ['version'];

/** Section used for keys without a section (e.g. "ProductName"). */
export const ROOT_SECTION = '(ohne Sektion)';

export const KeyStatus = {
  /** No override, the value comes from the main translation. */
  INHERITED: 'inherited',
  /** Override with an own value. */
  OVERRIDDEN: 'overridden',
  /** Override that repeats the main value, can be deleted. */
  IDENTICAL: 'identical',
  /** Override with an empty string, blanks out the main value. */
  EMPTY: 'empty',
  /** Override for a key the main translation does not know. */
  UNKNOWN: 'unknown',
};

export const STATUS_LABEL = {
  [KeyStatus.INHERITED]: 'geerbt',
  [KeyStatus.OVERRIDDEN]: 'überschrieben',
  [KeyStatus.IDENTICAL]: 'identisch',
  [KeyStatus.EMPTY]: 'leer',
  [KeyStatus.UNKNOWN]: 'unbekannter Key',
};

/** A status that needs attention before the merge request goes out. */
export const PROBLEM_STATUS = [
  KeyStatus.IDENTICAL,
  KeyStatus.EMPTY,
  KeyStatus.UNKNOWN,
];

const PLACEHOLDER_PATTERN = /{{[^{}]+}}/g;

export function isMetaKey(path) {
  return META_KEYS.includes(path);
}

/**
 * @name flattenTranslations
 * @description Turns a nested translation object into { 'a.b.c': 'value' }.
 * @param {Object} source nested translations
 * @param {string} prefix current path prefix (used internally)
 * @returns {Object.<string, string>} flat translations, original key order kept
 */
export function flattenTranslations(source, prefix = '') {
  const result = {};
  Object.entries(source || {}).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenTranslations(value, path));
    } else {
      result[path] = value === null || value === undefined ? '' : String(value);
    }
  });
  return result;
}

/**
 * @name unflattenTranslations
 * @description Rebuilds the nested structure the override files use.
 * @param {Object.<string, string>} flat flat translations
 * @returns {Object} nested translations, insertion order of `flat` kept
 */
export function unflattenTranslations(flat) {
  const result = {};
  Object.entries(flat).forEach(([path, value]) => {
    const parts = path.split('.');
    const last = parts.pop();
    let target = result;
    parts.forEach((part) => {
      if (!target[part] || typeof target[part] !== 'object') {
        target[part] = {};
      }
      target = target[part];
    });
    target[last] = value;
  });
  return result;
}

/**
 * @name getSection
 * @description First path segment of a key, used to group the keys in the UI.
 */
export function getSection(path) {
  const separator = path.indexOf('.');
  return separator === -1 ? ROOT_SECTION : path.slice(0, separator);
}

/**
 * @name getKeyStatus
 * @description Classifies a key for the status badge.
 */
export function getKeyStatus({
  hasMain,
  hasOverride,
  mainValue,
  overrideValue,
}) {
  if (!hasMain) return KeyStatus.UNKNOWN;
  if (!hasOverride) return KeyStatus.INHERITED;
  if (overrideValue === '') return KeyStatus.EMPTY;
  if (overrideValue === mainValue) return KeyStatus.IDENTICAL;
  return KeyStatus.OVERRIDDEN;
}

function getPlaceholders(value) {
  return (String(value || '').match(PLACEHOLDER_PATTERN) || []).sort();
}

/**
 * @name comparePlaceholders
 * @description Compares the {{placeholders}} of main value and override.
 * @returns {{missing: string[], added: string[]}} missing = dropped in the override
 */
export function comparePlaceholders(mainValue, overrideValue) {
  const main = getPlaceholders(mainValue);
  const override = getPlaceholders(overrideValue);
  return {
    missing: main.filter((token) => !override.includes(token)),
    added: override.filter((token) => !main.includes(token)),
  };
}

/**
 * @name diffOverrides
 * @description Compares the loaded override file with the current draft.
 * @param {Object.<string, string>} base values as loaded
 * @param {Object.<string, string>} draft values in the editor
 */
export function diffOverrides(base, draft) {
  const added = [];
  const changed = [];
  const removed = [];

  Object.keys(draft).forEach((key) => {
    if (!(key in base)) {
      added.push({ key, value: draft[key] });
    } else if (base[key] !== draft[key]) {
      changed.push({ key, from: base[key], to: draft[key] });
    }
  });

  Object.keys(base).forEach((key) => {
    if (!(key in draft)) {
      removed.push({ key, value: base[key] });
    }
  });

  return {
    added,
    changed,
    removed,
    count: added.length + changed.length + removed.length,
  };
}

/**
 * @name buildOverrideFile
 * @description Builds the file to download. Keys that were already in the file keep
 * their position, new keys follow the order of the main translation. That keeps the
 * git diff of the merge request small and reviewable.
 * @param {Object.<string, string>} draft current override values
 * @param {string[]} baseOrder key order of the loaded file
 * @param {string[]} mainOrder key order of the main translation
 * @returns {Object} nested override object
 */
export function buildOverrideFile(draft, baseOrder = [], mainOrder = []) {
  const ordered = {};
  const take = (key) => {
    if (key in draft && !(key in ordered)) {
      ordered[key] = draft[key];
    }
  };

  META_KEYS.forEach(take);
  baseOrder.forEach(take);
  mainOrder.forEach(take);
  Object.keys(draft).forEach(take);

  return unflattenTranslations(ordered);
}

/**
 * @name countOverrides
 * @description Number of real translation keys in a draft (meta keys excluded).
 */
export function countOverrides(draft) {
  return Object.keys(draft || {}).filter((path) => !isMetaKey(path)).length;
}

function shorten(value, length = 80) {
  const text = String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

/**
 * @name buildChangeSummary
 * @description Markdown summary of the draft, meant for the merge request description.
 */
export function buildChangeSummary({ className, language, diff }) {
  const lines = [
    `Translations \`${className}\` / \`${language}\``,
    `Datei: \`derivatives/${className}/${language}-translations.json\``,
    '',
  ];

  const block = (title, entries, format) => {
    if (!entries.length) return;
    lines.push(`${title} (${entries.length}):`);
    entries.forEach((entry) => lines.push(`- ${format(entry)}`));
    lines.push('');
  };

  block(
    'Neu',
    diff.added,
    (entry) => `\`${entry.key}\`: "${shorten(entry.value)}"`,
  );
  block(
    'Geändert',
    diff.changed,
    (entry) =>
      `\`${entry.key}\`: "${shorten(entry.from)}" → "${shorten(entry.to)}"`,
  );
  block(
    'Entfernt (fällt zurück auf die Haupt-Übersetzung)',
    diff.removed,
    (entry) => `\`${entry.key}\`: "${shorten(entry.value)}"`,
  );

  return lines.join('\n').trim();
}

/**
 * @name buildChangeSummaries
 * @description Markdown summary across several languages of one portal.
 * @param {string} className portal
 * @param {Array<{language: string, diff: Object}>} entries only changed languages
 */
export function buildChangeSummaries(className, entries) {
  return entries
    .filter((entry) => entry.diff.count)
    .map((entry) =>
      buildChangeSummary({
        className,
        language: entry.language,
        diff: entry.diff,
      }),
    )
    .join('\n\n');
}

/**
 * @name missingLanguages
 * @description Languages of a portal that do not override this key (yet).
 * @param {string} path key
 * @param {string[]} languages languages of the portal
 * @param {Object.<string, Object>} drafts language -> flat override map
 */
export function missingLanguages(path, languages, drafts) {
  return languages.filter((code) => drafts?.[code]?.[path] === undefined);
}

/**
 * @name findDeviatingKeys
 * @description Keys that some languages override and others do not – the set a
 * portal has to even out if it wants the same keys everywhere.
 */
export function findDeviatingKeys(paths, languages, drafts) {
  if (languages.length < 2) return [];
  return paths.filter((path) => {
    const missing = missingLanguages(path, languages, drafts).length;
    return missing > 0 && missing < languages.length;
  });
}
